import React, { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Link } from 'react-router-dom';

import { stringify as stringifyQuery } from 'query-string';

import download from 'downloadjs';

import AntdTable from 'antd/lib/table';
import AntdSelect from 'antd/lib/select';
import AntdButton from 'antd/lib/button';
import AntdResult from 'antd/lib/result';
import {
  UpOutlined,
  DownOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import { Tooltip } from 'hive-admin/src/components/Popover';

import Types from '../modules/types';

import tests from '../helpers/tests';

export function useSelectedCharts(groups) {
  const defaultSelectedCharts = useMemo(
    () => groups.reduce(
      (agr, { id, charts }) => {
        agr[id] = charts[0].id;
        return agr;
      },
      {},
    ),
    [groups],
  );
  const [selectedCharts, setSelectedCharts] = useState(defaultSelectedCharts);
  const setSelectedChart = useMemo(
    () => (groupId, chartId) => setSelectedCharts({
      ...selectedCharts,
      [groupId]: chartId,
    }),
    [selectedCharts, setSelectedCharts],
  );
  return [selectedCharts, setSelectedChart, setSelectedCharts];
}

export function useSelectedSorts(groups) {
  const defaultSelectedSorts = useMemo(
    () => groups.reduce(
      (agr, { id, sorts }) => {
        agr[id] = sorts ? sorts[0].id : 'default';
        return agr;
      },
      {},
    ),
    [groups],
  );
  const [selectedSorts, setSelectedSorts] = useState(defaultSelectedSorts);
  const setSelectedSort = useMemo(
    () => (groupId, sortId) => setSelectedSorts({
      ...selectedSorts,
      [groupId]: sortId,
    }),
    [selectedSorts, setSelectedSorts],
  );
  return [selectedSorts, setSelectedSort, setSelectedSorts];
}

export function useSelectedViews(groups) {
  const defaultSelectedViews = useMemo(
    () => groups.reduce(
      (agr, { id }) => {
        agr[id] = 'default';
        return agr;
      },
      {},
    ),
    [groups],
  );
  const [selectedViews, setSelectedViews] = useState(defaultSelectedViews);
  const setSelectedView = useMemo(
    () => (groupId, viewId) => setSelectedViews({
      ...selectedViews,
      [groupId]: viewId,
    }),
    [selectedViews, setSelectedViews],
  );
  return [selectedViews, setSelectedView, setSelectedViews];
}

export const ToggleExpandButton = styled(AntdButton)`
  width: 100%;
`;

export const ExpandGroupIcon = styled(DownOutlined)`
  transform: scale(0.5);
`;

export const CollapseGroupIcon = styled(UpOutlined)`
  transform: scale(0.5);
`;

export const DownloadIcon = styled(DownloadOutlined)`
  font-size: 20px;
  margin-left: 15px;
`;

export const GroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  flex: 1;
  transition: opacity 300ms, filter 300ms;
  opacity: ${({ loading }) => (loading ? 0.2 : 1)};
  ${({ loading }) => loading && css`
    pointer-events: none;
    filter: grayscale(1);
  `}
`;

export const GroupsLoading = styled(AntdResult)`

`;

GroupsLoading.defaultProps = {
  icon: <LoadingOutlined />,
};

export const GroupsError = styled(AntdResult)`

`;

export const GroupWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-top: 20px;
`;

export const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2ex;
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 2ex;
`;

export const Title = styled.div`
  display: flex;
  font-size: 34px;
  font-weight: 500;
  justify-content: center;
  align-items: center;
`;

export const Subtitle = styled.div`
  display: flex;
  margin-left: 3px;
`;

export const Switcher = styled(AntdSelect)`
  .ant-select-selection__rendered {
    margin-right: 34px;
  }
`;

Switcher.defaultProps = {
  dropdownMatchSelectWidth: false,
};

export const Groups = ({ groups, ...props }) => {
  const compared = ['1', '2'].includes(`${props.searchParams.compare}`);
  const comparedReversed = (
    !compared || `${props.searchParams.compare}` !== '2'
    ? false
    : true
  );
  const [selectedCharts, setSelectedChart] = useSelectedCharts(groups);
  const [selectedSorts, setSelectedSort] = useSelectedSorts(groups);
  const [selectedViews, setSelectedView] = useSelectedViews(groups);
  const response = useMemo(
    () => {
      if (!props.data || !props.data.data) {
        return {};
      }
      return groups.reduce(
        (agr, group) => {
          Object.keys(props.data.data).forEach((chartId) => {
            if (group.chartsMap[chartId] && props.data.data[chartId]) {
              const items = agr[chartId] = (
                group.sortsMap[selectedSorts[group.id]]
                .sort(props.data.data[chartId].data)
              );
              items.forEach((item, i) => {
                if (compared && !item.b) {
                  item.b = {
                    _id: item._id,
                    label: item.label,
                    value: 0,
                    proportion: 0,
                    proportionRelative: 0,
                    level: 0,
                    proportionOffset: (
                      !i ? 0 : items[i - 1].b.proportionOffset
                    ),
                    proportionOffsetRelative: (
                      !i ? 0 : items[i - 1].b.proportionOffsetRelative
                    ),
                  };
                }
                if (!i) {
                  item.proportionOffset = 0;
                  item.proportionOffsetRelative = 0;
                  if (item.b) {
                    item.b.proportionOffset = 0;
                    item.b.proportionOffsetRelative = 0;
                  }
                } else {
                  item.proportionOffset = (
                      items[i - 1].proportionOffset
                    + items[i - 1].proportion
                  );
                  item.proportionOffsetRelative = (
                      items[i - 1].proportionOffsetRelative
                    + items[i - 1].proportionRelative
                  );
                  if (item.b) {
                    item.b.proportionOffset = (
                        items[i - 1].b.proportionOffset
                      + items[i - 1].b.proportion
                    );
                    item.b.proportionOffsetRelative = (
                        items[i - 1].b.proportionOffsetRelative
                      + items[i - 1].b.proportionRelative
                    );
                  }
                }
              });
            }
            // agr[chartId] = props.data.data[chartId];
          });
          return agr;
        },
        {},
      );
    },
    [props.data, selectedSorts, compared, groups],
  );
  const [groupsExpanded, setGroupsExpanded] = useState(
    groups.map(({ id }) => id),
  );
  const groupsRender = groups.map((group) => {
    const { id, title, charts, sorts, chartsMap } = group;
    const selectedChartId = selectedCharts[id];
    const chart = chartsMap[selectedChartId];
    const data = response[selectedChartId];
    const isExpanded = groupsExpanded.includes(id);
    if (!data) {
      return null;
    }
    const { precision = 2 } = chart;
    return (
      <GroupWrapper key={id}>
        <Header>
          <HeaderLeft>
            <Title>
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (isExpanded) {
                    setGroupsExpanded(
                      groupsExpanded.filter(groupId => groupId !== id),
                    );
                  } else {
                    setGroupsExpanded([...groupsExpanded, id]);
                  }
                }}
              >
                {
                  isExpanded
                  ? <CollapseGroupIcon />
                  : <ExpandGroupIcon />
                }
                {title}
              </span>
              {
                tests.viewerIsAdminAndDoesNotHavePermission(
                  'reportsExport'
                )(props)
                ? null
                : (
                  <DownloadIcon
                    onClick={() => download(
                      `${[
                        (
                          [
                            'Name',
                            ...(compared ? ['Value A', 'Value B'] : ['Value']),
                            // ...(group.percentage === false ? [] : (
                            //   compared
                            //   ? ['Percentage A', 'Percentage B']
                            //   : ['Percentage']
                            // )),
                          ]
                          .join(', ')
                        ),
                        ...(
                          data
                          .map(row => (
                            [
                              !row.label?.length ? 'N/A' : row.label,
                              ...(
                                compared
                                ? (
                                    comparedReversed
                                    ? [row.b?.value, row.value]
                                    : [row.value, row.b?.value]
                                  )
                                : [row.value]
                              ).map(value => (value || 0).toFixed(precision)),
                              // ...(
                              //   group.percentage === false
                              //   ? []
                              //   : (
                              //         compared
                              //       ? (
                              //             comparedReversed
                              //           ? [row.b?.proportion, row.proportion]
                              //           : [row.proportion, row.b?.proportion]
                              //         )
                              //       : [row.proportion]
                              //     )
                              // ).map(value => ((value || 0) * 100).toFixed(2)),
                            ]
                            .map(column => `${column}`.replace(/"*/g, ''))
                            .join(', ')
                          ))
                        ),
                      ].join('\n')}`,
                      `${title} (${chart.label}).csv`,
                      'text/csv',
                    )}
                  />
                )
              }
            </Title>
            <Subtitle>
              {chart.title}
            </Subtitle>
          </HeaderLeft>
          <HeaderRight>
            {
              group.views === false
              ? null
              : (
                  <Switcher
                    value={selectedViews[group.id]}
                    onChange={value => setSelectedView(group.id, value)}
                  >
                    <Switcher.Option value="default">
                      List
                    </Switcher.Option>
                    <Switcher.Option value="stacked">
                      Stack
                    </Switcher.Option>
                  </Switcher>
                )
            }
            {
              sorts && sorts.length > 1
              ? (
                  <Switcher
                    value={selectedSorts[group.id]}
                    onChange={value => setSelectedSort(group.id, value)}
                  >
                    {sorts.map(({ id: value, label }) => (
                      <Switcher.Option key={value} value={value}>
                        {label}
                      </Switcher.Option>
                    ))}
                  </Switcher>
                )
              : null
            }
            {
              charts.length > 1
              ? (
                  <Switcher
                    value={selectedChartId}
                    onChange={value => setSelectedChart(id, value)}
                  >
                    {charts.map(({ id: value, label }) => (
                      <Switcher.Option key={value} value={value}>
                        {label}
                      </Switcher.Option>
                    ))}
                  </Switcher>
                )
              : null
            }
          </HeaderRight>
        </Header>
        {
          isExpanded
          ? chart.renderChart(
              data,
              chart,
              group,
              compared,
              comparedReversed,
              selectedViews[group.id],
              props,
            )
          : null
        }
      </GroupWrapper>
    );
  });
  return groupsRender;
};

export const Table = styled(AntdTable)`
  margin-top: 15px;

  .ant-table-body {
    margin: 0px !important;
    padding: 0px !important;
  }
  .ant-table-footer {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
  .ant-table {
    border: 0px !important;
  }
  .ant-table-title {
    border-bottom: 0px !important;
  }
  tr:not(:hover) > td {
    background: transparent !important;
  }
  td, th {
    padding: 10px 15px !important;
    /* padding: 4px 6px !important; */
    /* border: 0px !important; */
  }
  &:not(:first-child) {
    /* margin-top: 20px; */
    /* @media (min-width: 1200px) {
      margin-left: 20px;
    } */
  }
  &:last-child {
    margin-bottom: 20px;
  }

  @media (max-width: 1024px) {
    table {
      table-layout: fixed !important;
    }
    
    tr {
      position: relative;
      transform: scale(1); // Safari Hack
    }

    td {
      width: 100%;
    }

    .ant-table-footer {
      border-top: 0px !important;
    }
    .ant-table-footer:before {
      display: none !important;
    }
  }
`;

export const TableBarWrapper = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 1024px) {
    gap: 10px;
    justify-content: space-between;
  }
`;

export const TableBarComparedWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableBarSideWrapper = styled.div`
  padding-right: 20px;
  padding-left: 10px;
`;

// eslint-disable-next-line arrow-body-style
export const TableBarSideLetter = styled.div.attrs((props) => {
  return {
    style: {
      fontSize: props.compared ? '88%' : undefined,
      transform: props.compared ? 'translateY(2px)' : undefined,
    },
  };
})`
  text-align: right;
  white-space: nowrap;
  /* font-weight: 600; */
  flex-shrink: 0;
  /* color: ${({ theme, isFirst = false }) => (
    isFirst
    ? theme.less.primaryColor
    : (
        theme.less.primaryColor === theme.less.secondaryColor
        ? 'inherit'
        : theme.less.secondaryColor
      )
  )};
  opacity: ${({ isFirst = false, theme }) => (
    isFirst
    ? '1'
    : (
        theme.less.primaryColor === theme.less.secondaryColor
         ? '0.5'
         : '1'
      )
  )}; */
`;

// eslint-disable-next-line arrow-body-style
export const TableBarCount = styled.div.attrs((props) => {
  return {
    style: {
      fontSize: props.compared ? '88%' : undefined,
      transform: props.compared ? 'translateY(2px)' : undefined,
    },
  };
})`
  min-width: 80px;
  text-align: right;
  padding-right: 10px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    min-width: none;
  }
`;

// eslint-disable-next-line arrow-body-style
export const TableBarPercentage = styled.div.attrs((props) => {
  return {
    style: {
      fontSize: props.compared ? '88%' : undefined,
      transform: props.compared ? 'translateY(2px)' : undefined,
    },
  };
})`
  min-width: 60px;
  text-align: right;
  padding-right: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.4;
`;

export const TableBarValueWrapper = styled.div`
  flex: 1;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    position: absolute;
    inset: 0;
    
    // Safari Hack
    height: 43px;
    &:has(> :last-child:nth-child(2)) {
      height: 57px;
    }
  }
`;

export const TableBarValueLink = styled(Link)`

`;

// eslint-disable-next-line arrow-body-style
export const TableBarValueInner = styled.div.attrs((props) => {
  return {
    style: {
      width: `${(props.scale * 100).toFixed(2)}%`,
      marginLeft: (
        props.offset
        ? `${props.offset * 100}%`
        : '0px'
      ),
      // marginTop: localProps => (
      //   localProps.isCompare
      //   ? '8px !important'
      //   : undefined
      // ),
      opacity: (
          !props.scale
        ? 0
        : (!props.isFirst)
        ? (
            props.theme.less.primaryColor === props.theme.less.secondaryColor
            ? 0.2
            : 1
          )
        : 1
      ),
      transform: `${[
        props.compared ? 'translateY(1px)' : 'translateY(-1px)',
      ].filter(a => a).join(' ')}`,
    },
  };
})`
  display: flex;
  transform-origin: 0% center;
  background: ${({
    isFirst,
    theme,
  }) => (
    isFirst
    ? theme.less.primaryColor
    : (
        theme.less.primaryColor === theme.less.secondaryColor
        ? theme.less.textColor
        : theme.less.secondaryColor
      )
  )};
  border-radius: ${({ theme }) => theme.less.borderRadius};
  transition: width 300ms cubic-bezier(0.25, 1, 0.5, 1),
    opacity 300ms,
    background 300ms 300ms;
  &:not([data-value="0"]),
  &[data-value="0"] {
    width: 0px;
    min-width: 0px !important;
    margin-left: 0px;
    transform: translateY(1px);
  }
  &:not(:first-child) {
    margin-top: 9px;
  }

  height: ${({ isCompare }) => (isCompare ? '8px' : '14px')};
  margin-top: ${({ isCompare }) => (isCompare ? '8px !important' : undefined)};

  @media (max-width: 1024px) {
    border-radius: 0px !important;
    margin-top: 0px !important;
    opacity: 0.1 !important;

    height: ${({ compared, isCompare }) => (
      compared
        ? isCompare ? '30%' : '70%'
        : '100%'
    )} !important;
  }
`;

export function TableBarValue({
  title,
  searchParams,
  comparedIndex,
  alternative,
  isFirst,
  ...props
}) {
  const toggleCompareValue = (
      !searchParams.compare
    ? '0'
    : alternative
    ? '1'
    : '2'
  );
  return (
    title
    ? (
        <Tooltip
          title={title}
          destroyTooltipOnHide
        >
          <Link
            to={`?${stringifyQuery({
              ...searchParams,
              compare: `${toggleCompareValue}`,
            })}`}
          >
            <TableBarValueInner
              {...props}
              isFirst={isFirst}
              alternative={alternative}
            />
          </Link>
        </Tooltip>
      )
    : (
        <TableBarValueInner
          {...props}
          isFirst={isFirst}
          alternative={alternative}
        />
      )
  );
}

export const TableLabel = styled.div`
  white-space: nowrap;
`;

export function TableChart({
  data,
  precision = 2,
  percentage = true,
  view = 'default',
  skipEmpty = false,
  // title = '',
  compared = false,
  comparedReversed,
  maxItems,
  pageProps,
}) {
  const [showAllData, setShowAllData] = useState(false);
  const dataVisible = useMemo(
    () => {
      const dataFiltered = (
        skipEmpty
        ? data.filter(record => (
            record.value > 0
            && (!compared || record.b?.value > 0)
          ))
        : data
      );
      return (
        (
          !showAllData
          && Number.isFinite(maxItems)
          && dataFiltered?.length > maxItems
        )
        ? dataFiltered.slice(0, maxItems)
        : dataFiltered
      );
    },
    [showAllData, compared, skipEmpty, data, maxItems],
  );
  return (
    <Table
      bordered={false}
      // title={() => }
      showHeader={false}
      pagination={false}
      rowKey="_id"
      dataSource={dataVisible}
      size="small"
      footer={() => (
        data?.length > maxItems
        ? (
            <ToggleExpandButton
              onClick={() => setShowAllData(!showAllData)}
              icon={showAllData ? 'up' : 'down'}
            >
              {
                showAllData
                ? 'Show Less'
                : 'Show More'
              }
            </ToggleExpandButton>
          )
        : null
      )}
      columns={[
        {
          key: '0',
          title: null,
          render: record => (
            <TableLabel>
              {
                  record.label?.length
                ? record.label
                : <span style={{ opacity: 0.5 }}>N/A</span>
              }
            </TableLabel>
          ),
        },
        {
          key: '1',
          width: '100%',
          title: null,
          render: record => {
            const valueString = Types.decimalize(record.value, '.', precision);
            const valueTitle = (
              <span>
                {`${record.label || '- '}:`}
                &nbsp;
                <strong>
                  {valueString}
                </strong>
              </span>
            );
            const valueBString = (
                record.b
              ? Types.decimalize(record.b.value || 0, '.', precision)
              : '-'
            );
            const valueBTitle = (
              <span>
                {`${record.label || '- '}:`}
                &nbsp;
                <strong>
                  {valueBString}
                </strong>
              </span>
            );
            return (
              <TableBarWrapper>
                <TableBarComparedWrapper>
                  <TableBarCount compared={compared}>
                    {valueString}
                  </TableBarCount>
                  {
                    compared
                    ? (
                        <TableBarCount compared>
                          {valueBString}
                        </TableBarCount>
                      )
                    : null
                  }
                </TableBarComparedWrapper>
                {
                  percentage
                  ? (
                      <TableBarComparedWrapper>
                        <TableBarPercentage compared={compared}>
                          {`${Types.decimalize(
                              record.proportion * 100, '.', 1
                          )} %`}
                        </TableBarPercentage>
                        {
                          compared
                          ? (
                              <TableBarPercentage compared>
                                {
                                  record.b
                                  ? `${Types.decimalize(
                                      record.b.proportion * 100,
                                      '.',
                                      1,
                                    )} %`
                                  : '-'
                                }
                              </TableBarPercentage>
                            )
                          : null
                        }
                      </TableBarComparedWrapper>
                    )
                  : null
                }
                {
                  compared
                  ? (
                      <TableBarSideWrapper key="side">
                        <TableBarSideLetter
                          compared={compared}
                          isFirst={!comparedReversed}
                        >
                          {comparedReversed ? 'B' : 'A'}
                        </TableBarSideLetter>
                        <TableBarSideLetter
                          compared={compared}
                          isFirst={comparedReversed}
                        >
                          {comparedReversed ? 'A' : 'B'}
                        </TableBarSideLetter>
                      </TableBarSideWrapper>
                    )
                  : null
                }
                <TableBarValueWrapper>
                  <TableBarValue
                    title={valueTitle}
                    scale={
                      view === 'stacked'
                      ? record.proportionRelative
                      : record.level
                    }
                    offset={
                      view === 'stacked'
                      ? record.proportionOffsetRelative
                      : 0
                    }
                    data-value={record.value || 0}
                    compared={compared}
                    // pageProps={pageProps}
                    searchParams={pageProps.searchParams}
                    isFirst={!comparedReversed}
                    comparedIndex={comparedReversed ? 1 : 0}
                  />
                  {
                    compared
                    ? (
                        <TableBarValue
                          title={valueBTitle}
                          scale={
                            record.b
                            ? (
                                view === 'stacked'
                                ? record.b.proportionRelative
                                : record.b.level
                              )
                            : 0
                          }
                          offset={
                            view === 'stacked'
                            ? record.b.proportionOffsetRelative
                            : 0
                          }
                          data-value={record.b ? (record.b.value || 0) : 0}
                          compared
                          searchParams={pageProps.searchParams}
                          alternative
                          isCompare
                          isFirst={comparedReversed}
                          comparedIndex={comparedReversed ? 1 : 0}
                        />
                      )
                    : null
                  }
                </TableBarValueWrapper>
              </TableBarWrapper>
            );
          },
        },
      ]}
    />
  );
}
